import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Upload, MapPin, ImageIcon } from 'lucide-react'
import { uploadImageToStorageSecure } from '@/lib/secureFileUpload'
import { BannerImageCropper, type CropData } from '@/components/shared/BannerImageCropper'
import { AddressAutocomplete, type AddressSearchResult } from '@/components/shared/AddressAutocomplete'
import type { ArtistOnboarding } from '@/types/models'

interface ArtistOnboardingStep1Props {
  onboarding: ArtistOnboarding
  onDataChange: (data: Partial<ArtistOnboarding>) => void
}

// Helper function to process escaped newlines in text
const processNewlines = (text: string) => {
  return text.split('\\n').join('\u2028')
}

export function ArtistOnboardingStep1({ onboarding, onDataChange }: ArtistOnboardingStep1Props) {
  const [formData, setFormData] = useState({
    displayName: onboarding.displayName || '',
    firstName: onboarding.firstName || '',
    lastName: onboarding.lastName || '',
    bio: onboarding.bio || '',
    location: onboarding.location || '',
    lat: onboarding.lat,
    lon: onboarding.lon,
    profileImageUrl: onboarding.profileImageUrl || '',
    backdropUrl: onboarding.backdropUrl || ''
  })
  
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(null)
  const [profileUploading, setProfileUploading] = useState(false)
  const [profileUploadError, setProfileUploadError] = useState<string | null>(null)
  const [bannerImageFile, setBannerImageFile] = useState<File | null>(null)
  const [bannerImagePreview, setBannerImagePreview] = useState<string | null>(null)
  const [bannerUploading, setBannerUploading] = useState(false)
  const [bannerUploadError, setBannerUploadError] = useState<string | null>(null)
  const [showBannerCropper, setShowBannerCropper] = useState(false)
  const [geocodingError, setGeocodingError] = useState<string | null>(null)
  const [isBioExpanded, setIsBioExpanded] = useState(false)

  const BIO_CHARACTER_LIMIT = 150

  // Helper function to determine if bio should be truncated
  const shouldTruncateBio = (bio: string) => {
    return bio.length > BIO_CHARACTER_LIMIT
  }

  // Update parent component when form data changes
  useEffect(() => {
    onDataChange(formData)
  }, [formData]) // Remove onDataChange from dependencies to prevent infinite loop

  const handleInputChange = (field: keyof typeof formData, value: string | number | undefined) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleProfileImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setProfileUploadError(null)
    setProfileUploading(true)

    try {
      // Create preview with FileReader for immediate display
      const reader = new FileReader()
      reader.onload = (e) => {
        const result = e.target?.result as string
        setProfileImagePreview(result)
      }
      reader.readAsDataURL(file)

      // Upload to Firebase Storage using onboardings path (allows unauthenticated uploads)
      const onboardingIdentifier = onboarding.id || onboarding.userId || onboarding.displayName
      const firebaseUrl = await uploadImageToStorageSecure(
        file,
        `onboardings/${onboardingIdentifier}/profile`,
        onboardingIdentifier
      )
      
      // Update form data with Firebase URL (not the data URL!)
      handleInputChange('profileImageUrl', firebaseUrl)
    } catch (error) {
      console.error('Failed to upload profile image:', error)
      setProfileUploadError('Failed to upload profile image. Please try again.')
      setProfileImagePreview(null)
    } finally {
      setProfileUploading(false)
    }
  }

  const handleBannerImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setBannerUploadError(null)
    setBannerImageFile(file)
    setShowBannerCropper(true)
  }

  const handleBannerCropComplete = async (croppedImageUrl: string, _cropData: CropData) => {
    setBannerUploading(true)
    setShowBannerCropper(false)

    try {
      // Convert the cropped image URL to a File for upload
      const response = await fetch(croppedImageUrl)
      const blob = await response.blob()
      const croppedFile = new File([blob], `banner-${Date.now()}.jpg`, { type: 'image/jpeg' })

      // Upload to Firebase Storage using onboardings path (allows unauthenticated uploads)
      const onboardingIdentifier = onboarding.id || onboarding.userId || onboarding.displayName
      const firebaseUrl = await uploadImageToStorageSecure(
        croppedFile,
        `onboardings/${onboardingIdentifier}/banner`,
        onboardingIdentifier
      )
      
      // Update form data with Firebase URL
      handleInputChange('backdropUrl', firebaseUrl)
      setBannerImagePreview(croppedImageUrl)
    } catch (error) {
      console.error('Failed to upload banner image:', error)
      setBannerUploadError('Failed to upload banner image. Please try again.')
      setBannerImageFile(null)
      setBannerImagePreview(null)
    } finally {
      setBannerUploading(false)
    }
  }

  const handleBannerCropCancel = () => {
    setShowBannerCropper(false)
    setBannerImageFile(null)
    setBannerUploadError(null)
  }

  const handleAddressSelect = (place: AddressSearchResult) => {
    setFormData(prev => ({
      ...prev,
      location: place.address,
      lat: place.lat,
      lon: place.lon
    }))
    setGeocodingError(null)
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-semibold">
              2
            </div>
            Profile Information
          </CardTitle>
          <CardDescription>
            Review and update your profile information. This will be visible to potential clients.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Profile Image */}
          <div className="space-y-3">
            <Label>Profile Image</Label>
            <div className="flex items-center gap-6">
              <img
                src={profileImagePreview || formData.profileImageUrl}
                alt={formData.displayName}
                className="w-20 h-20 rounded-full object-cover border-2 border-border"
                onError={(e) => {
                  const target = e.target as HTMLImageElement
                  target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(formData.displayName || 'Artist')}&background=random`
                }}
              />
              <div className="flex-1 space-y-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleProfileImageChange}
                  className="hidden"
                  id="profile-image-upload"
                  disabled={profileUploading}
                />
                <label
                  htmlFor="profile-image-upload"
                  className={`inline-flex items-center gap-2 px-4 py-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md cursor-pointer text-sm font-medium transition-colors ${
                    profileUploading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <Upload className="w-4 h-4" />
                  {profileUploading ? 'Uploading...' : 'Upload New Image'}
                </label>
                <p className="text-xs text-muted-foreground">
                  Recommended: Square image, at least 400x400 pixels
                </p>
                {formData.profileImageUrl && !profileUploadError && (
                  <p className="text-xs text-green-600">
                    ✓ Profile image uploaded successfully
                  </p>
                )}
                {profileUploadError && (
                  <p className="text-xs text-destructive">{profileUploadError}</p>
                )}
              </div>
            </div>
          </div>

          {/* Banner Image */}
          <div className="space-y-3">
            <Label>Banner Image</Label>
            <div className="space-y-3">
              {/* Banner Preview */}
              <div className="relative w-full h-32 rounded-lg overflow-hidden border-2 border-border bg-gradient-to-r from-purple-500 to-pink-500">
                {(bannerImagePreview || formData.backdropUrl) && (
                  <img
                    src={bannerImagePreview || formData.backdropUrl}
                    alt="Banner preview"
                    className="w-full h-full object-cover"
                  />
                )}
                {!bannerImagePreview && !formData.backdropUrl && (
                  <div className="absolute inset-0 flex items-center justify-center text-white">
                    <div className="text-center">
                      <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-70" />
                      <p className="text-sm opacity-70">Banner preview</p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Upload Controls */}
              <div className="space-y-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleBannerImageChange}
                  className="hidden"
                  id="banner-image-upload"
                  disabled={bannerUploading}
                />
                <label
                  htmlFor="banner-image-upload"
                  className={`inline-flex items-center gap-2 px-4 py-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md cursor-pointer text-sm font-medium transition-colors ${
                    bannerUploading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <Upload className="w-4 h-4" />
                  {bannerUploading ? 'Uploading...' : 'Upload Banner Image'}
                </label>
                
                <p className="text-xs text-muted-foreground">
                  Best case: Photo of you during a tattoo session or your studio. You'll be able to crop it to the perfect size.
                </p>
                
                {formData.backdropUrl && !bannerUploadError && (
                  <p className="text-xs text-green-600">
                    ✓ Banner image uploaded and cropped
                  </p>
                )}
                
                {bannerUploadError && (
                  <p className="text-xs text-destructive">{bannerUploadError}</p>
                )}
              </div>
            </div>
          </div>

          {/* Name Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                placeholder="Your first name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                placeholder="Your last name"
              />
              <p className="text-xs text-muted-foreground">
                Only the first letter will be shown publicly (e.g., "Smith" → "S.")
              </p>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="bio">Description *</Label>
            <Textarea
              id="bio"
              value={formData.bio}
              onChange={(e) => handleInputChange('bio', e.target.value)}
              placeholder="Tell potential clients about your style, experience, and what makes your work special..."
              rows={4}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              {formData.bio.length}/500 characters • Tip: You can write a lot longer description than on Instagram, eg about your style, experience, and what makes your work special! Please no usernames, Instagram names, emails, or location (location is displayed below).
            </p>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location">Studio Location *</Label>
            <AddressAutocomplete
              value={formData.location}
              onChange={(value) => handleInputChange('location', value)}
              onSelect={handleAddressSelect}
              placeholder="Start typing your studio address..."
            />
            <p className="text-xs text-muted-foreground">
              Start typing your address and select from the suggestions for accurate location data.
            </p>
            {geocodingError && (
              <p className="text-xs text-destructive">{geocodingError}</p>
            )}
            {formData.lat && formData.lon && (
              <div className="text-xs text-muted-foreground bg-muted p-2 rounded">
                <strong>Coordinates:</strong> {formData.lat.toFixed(4)}, {formData.lon.toFixed(4)}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Mobile Artist Page Preview */}
      <Card>
        <CardHeader>
          <CardTitle>Mobile Artist Page Preview</CardTitle>
          <CardDescription>
            This is how your profile will appear to potential clients on their mobile devices
          </CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center p-4">
          {/* Mobile Frame */}
          <div className="w-full max-w-sm mx-auto bg-background border border-border rounded-lg overflow-hidden shadow-lg">
            {/* Header Section with Banner */}
            <div className="relative w-full" style={{ height: 'var(--profile-header-height)' }}>
              {/* Background Image */}
              <div 
                className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 bg-cover bg-center phone-container-banner"
                style={{
                  backgroundImage: (bannerImagePreview || formData.backdropUrl) ? `url(${bannerImagePreview || formData.backdropUrl})` : undefined,
                  backgroundBlendMode: (bannerImagePreview || formData.backdropUrl) ? 'overlay' : 'normal',
                  zIndex: 1,
                }}
              />
            </div>

            {/* Avatar, Name, and Display Name (left-aligned, overlapping header) */}
            <div
              className="phone-container-padded flex items-end gap-4 relative z-10"
              style={{ marginTop: 'calc(-1 * var(--profile-avatar-size) / 3)' }}
            >
              <img
                src={profileImagePreview || formData.profileImageUrl}
                alt={formData.displayName}
                className="profile-avatar object-cover shadow-lg border-4 border-background"
                style={{ zIndex: 10 }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement
                  target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(formData.firstName || formData.displayName || 'Artist')}&background=random`
                }}
              />
              <div className="flex flex-col justify-end">
                {formData.firstName && formData.lastName ? (
                  <h1 className="text-profile-title text-foreground">
                    {formData.firstName} {formData.lastName.charAt(0)}.
                  </h1>
                ) : (
                  <h1 className="text-profile-title text-muted-foreground">
                    Enter your name
                  </h1>
                )}
                <div className="mt-1">
                  <h2 className="text-profile-subtitle text-muted-foreground">
                    @{formData.displayName || 'instagram_username'}
                  </h2>
                </div>
              </div>
            </div>
            
            {/* Content Section */}
            <div
              className="phone-container-padded pt-0"
              style={{ marginTop: 'calc(var(--profile-avatar-size) / 3 + 1rem)' }}
            >
              {/* Bio */}
              {formData.bio ? (
                <div className="mb-4 -mt-8">
                  <div className="relative">
                    <p 
                      className={`text-foreground text-lg text-profile-bio whitespace-pre-wrap ${
                        shouldTruncateBio(formData.bio) && !isBioExpanded ? 'max-h-[8.5em] overflow-hidden' : ''
                      }`}
                    >
                      {processNewlines(formData.bio)}
                    </p>
                    {shouldTruncateBio(formData.bio) && !isBioExpanded && (
                      <div 
                        className="absolute bottom-0 left-0 right-0 h-[2em] bg-gradient-to-b from-transparent to-background"
                        aria-hidden="true"
                      />
                    )}
                  </div>
                  {shouldTruncateBio(formData.bio) && (
                    <button
                      onClick={() => setIsBioExpanded(!isBioExpanded)}
                      className="mt-2 text-primary hover:text-primary/80 text-lg font-medium flex items-center gap-1"
                    >
                      {isBioExpanded ? (
                        <>
                          Show less
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="transform rotate-180"
                          >
                            <path d="m6 9 6 6 6-6"/>
                          </svg>
                        </>
                      ) : (
                        <>
                          Show more
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="m6 9 6 6 6-6"/>
                          </svg>
                        </>
                      )}
                    </button>
                  )}
                </div>
              ) : (
                <div className="mb-4 -mt-8">
                  <p className="text-foreground text-lg text-profile-bio text-muted-foreground italic">
                    Your bio will appear here...
                  </p>
                </div>
              )}

              {/* Location with Map Icon */}
              {formData.location && (
                <div className="mb-6 -mt-2">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="w-5 h-5" />
                    <span className="text-base">
                      {formData.location}
                    </span>
                  </div>
                </div>
              )}

              {/* 2-pill Navigation Menu */}
              <div className="my-4 flex justify-center">
                <div className="flex gap-2">
                  <div className="rounded-full px-6 py-2 text-sm font-medium bg-primary text-primary-foreground">
                    Flash
                  </div>
                  <div className="rounded-full px-6 py-2 text-sm font-medium text-muted-foreground">
                    On Skin
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Banner Cropper Modal */}
      {showBannerCropper && bannerImageFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <BannerImageCropper
            imageFile={bannerImageFile}
            onCropComplete={handleBannerCropComplete}
            onCancel={handleBannerCropCancel}
          />
        </div>
      )}
    </div>
  )
}
